/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IAddContactInfo, ICheckoutGuestProfileViewProps, IShowContactInfo } from './lds-checkout-contact-section';
// @ts-ignore
import { Secondary, Ghost } from '@churchofjesuschrist/eden-buttons';
// @ts-ignore
import { Error } from '@churchofjesuschrist/eden-alert';

interface ILdsAddContactInfo extends IAddContactInfo {
    phone: string;
    phoneError: boolean;
    onChangePhone(phone: string, country?: string): void;
    isPhoneRequired: any;
    country?: any;
    resources?: any;
}

interface ILdsShowContactInfo extends IShowContactInfo {
    phone: string;
    resources?: any;
}

export const ShowContactInfo: React.FC<ILdsShowContactInfo> = ({ showInfoProps, email, editButton, phone }) => (
    <Node {...showInfoProps}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontSize: '16px', lineHeight: '21px', textTransform: 'none' }}>{phone}</span>
            {email}
        </div>
        {editButton}
    </Node>
);

export const AddContactInfo: React.FC<ILdsAddContactInfo> = props => {
    const {
        addFormProps,
        submitButton,
        cancelButton,
        inputGroupProps,
        label,
        error,
        input,
        onChangePhone,
        phone,
        resources,
        // isPhoneRequired,
        country,
        phoneError
    } = props;
    const titleStyle: React.CSSProperties = {
        fontFamily: `"Ensign:Sans",Arial,"noto sans" ,sans-serif`,
        fontStyle: 'normal',
        fontWeight: 600,
        fontSize: '18px',
        lineHeight: '22px',
        paddingBottom: '8px'
    };
    const descriptionStyle: React.CSSProperties = {
        fontFamily: `"Ensign:Sans",Arial,"noto sans" ,sans-serif`,
        fontStyle: 'normal',
        fontWeight: 400,
        fontSize: '14px',
        lineHeight: '19px',
        paddingBottom: '8px'
    };

    return (
        <Node {...addFormProps}>
            <div style={titleStyle}>{resources.contactInformationHeader}</div>
            <div style={descriptionStyle}>{resources.contactInformationExplanation}</div>
            <Node {...inputGroupProps}>
                <label id='ms-checkout-guest-profile__label' className='ms-checkout-guest-profile__input-label'>
                    {resources.phoneLabel}
                </label>
                {phoneError && (
                    <div style={{ width: '100%', marginBottom: '15px' }}>
                        {/* @ts-ignore */}
                        <Error>{resources.phoneRequiredError}</Error>
                    </div>
                )}
                <div style={{ width: '100%' }}>
                    <input
                        className='ms-checkout-guest-profile__input-text form-control'
                        style={{
                            border: '1px solid #878A8C',
                            boxSizing: 'border-box',
                            borderRadius: '2px',
                            padding: '8px',
                            width: '100%',
                            maxWidth: 'none'
                        }}
                        type='text'
                        onChange={e => onChangePhone(e.target.value, country)}
                        value={phone}
                    />
                </div>
            </Node>
            <Node {...inputGroupProps}>
                {label}
                {/* {error} */}
                {/* @ts-ignore */}
                {error && error.props && error.props.children && (
                    <div style={{ width: '100%', marginBottom: '15px' }}>
                        {/* @ts-ignore */}
                        <Error>{error.props.children}</Error>
                    </div>
                )}
                <input
                    className='ms-checkout-guest-profile__input-text form-control'
                    // @ts-ignore
                    onChange={e => input.props.onChange(e)}
                    style={{
                        border: '1px solid #878A8C',
                        boxSizing: 'border-box',
                        borderRadius: '2px',
                        padding: '8px',
                        width: '100%',
                        maxWidth: 'none'
                    }}
                    // @ts-ignore
                    value={input.props.value}
                    type='text'
                />
                {/* {input} */}
            </Node>
            <div style={{ display: 'flex', flexDirection: 'row', width: '100%', justifyContent: 'space-around', marginTop: '20px' }}>
                <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                    {cancelButton && (
                        // @ts-ignore
                        <Ghost style={{ width: '100%' }} onClick={() => cancelButton.props.onClick()}>
                            {/* @ts-ignore */}
                            {cancelButton.props.title}
                        </Ghost>
                    )}
                </div>
                <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                    {submitButton && (
                        // @ts-ignore
                        <Secondary style={{ width: '100%' }} onClick={() => submitButton.props.onClick()}>
                            {/* @ts-ignore */}
                            {submitButton.props.title}
                        </Secondary>
                    )}
                </div>
            </div>
            {/* {submitButton}
            {cancelButton} */}
        </Node>
    );
};

const CheckoutGuestProfileView: React.FC<ICheckoutGuestProfileViewProps> = props => {
    const [country, setCountry] = React.useState<any>();
    const { moduleProps, checkoutErrorRef, alert, showContactInfo, addContactInfo, onChangePhone, phone, phoneError, resources } = props;
    let isPhoneRequired = false;
    if (
        props.data.churchRetailChannelCountry &&
        props.data.churchRetailChannelCountry[0] &&
        props.data.churchRetailChannelCountry[0].PHONENUMBERREQ > 0
    ) {
        const phoneNumberReq = props.data.churchRetailChannelCountry[0].PHONENUMBERREQ;
        isPhoneRequired = phoneNumberReq === 1;
    }
    React.useEffect(() => {
        if (
            props.data.checkout &&
            // @ts-ignore
            props.data.checkout.result.checkoutCart &&
            // @ts-ignore
            props.data.checkout.result.checkoutCart &&
            // @ts-ignore
            props.data.checkout.result.checkoutCart.cart &&
            // @ts-ignore
            props.data.checkout.result.checkoutCart.cart.ExtensionProperties
        ) {
            // @ts-ignore
            const userCountry = props.data.checkout.result.checkoutCart.cart.ExtensionProperties.filter((value: any) => {
                return value.Key === 'Country';
            });

            if (userCountry) {
                setCountry(userCountry[0].Value?.StringValue);
            }
        }
    }, []);

    return (
        <Module {...moduleProps} ref={checkoutErrorRef}>
            {alert}
            {showContactInfo && <ShowContactInfo {...showContactInfo} phone={phone} resources={resources} />}
            {addContactInfo && (
                <AddContactInfo
                    {...addContactInfo}
                    onChangePhone={onChangePhone}
                    phone={phone}
                    phoneError={phoneError}
                    resources={resources}
                    isPhoneRequired={isPhoneRequired}
                    country={country}
                />
            )}
        </Module>
    );
};

export default CheckoutGuestProfileView;
